import { Error as ErrorIcon, Person } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { DisplayRow, UploadResult, UploadStatus } from "./types";

interface PreviewPhaseProps {
  displayRows: DisplayRow[];
  columns: GridColDef[];
  uploadStatus: UploadStatus;
  userCreationResults: UploadResult[];
  existingRows: DisplayRow[];
  newRows: DisplayRow[];
  invalidRows: DisplayRow[];
  duplicatesRemoved: number;
  fileName: string;
  onResetFile: () => void;
  processRowUpdate: (row: DisplayRow) => DisplayRow;
  onProcessRowUpdateError: (error: unknown) => void;
}

export const PreviewPhase = ({
  displayRows,
  columns,
  uploadStatus,
  userCreationResults,
  existingRows,
  newRows,
  invalidRows,
  duplicatesRemoved,
  fileName,
  onResetFile,
  processRowUpdate,
  onProcessRowUpdateError,
}: PreviewPhaseProps) => {
  const creationSuccessCount = userCreationResults.filter(
    (r) => !r.error,
  ).length;
  const creationFailCount = userCreationResults.filter((r) => r.error).length;

  return (
    <>
      {/* Step 1 results banner — shown during users_done review */}
      {uploadStatus === "users_done" && (
        <Alert
          severity={
            newRows.length === 0
              ? "info"
              : creationFailCount === 0
                ? "success"
                : "warning"
          }
        >
          <strong>Step 1 complete —</strong>{" "}
          {newRows.length === 0 ? (
            "No new users to create."
          ) : (
            <>
              <strong>{creationSuccessCount}</strong> user
              {creationSuccessCount !== 1 ? "s" : ""} created
              {creationFailCount > 0 && (
                <>
                  , <strong>{creationFailCount}</strong> failed
                </>
              )}
              .
            </>
          )}
          {existingRows.length > 0 && (
            <>
              {" "}
              {existingRows.length} existing user
              {existingRows.length !== 1 ? "s" : ""} will receive permissions
              below.
            </>
          )}{" "}
          Review permissions and click <strong>Apply Permissions</strong>.
        </Alert>
      )}

      {/* Toolbar */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {uploadStatus === "idle" && (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              {fileName}
            </Typography>
            <Button size="small" onClick={onResetFile}>
              Change File
            </Button>
          </>
        )}
        <Box
          sx={{
            ml: uploadStatus === "idle" ? "auto" : 0,
            display: "flex",
            gap: 1,
          }}
        >
          {newRows.length > 0 && (
            <Chip
              label={`${newRows.length} new`}
              color="success"
              size="small"
            />
          )}
          {existingRows.length > 0 && (
            <Chip
              label={`${existingRows.length} existing`}
              color="primary"
              size="small"
            />
          )}
          {invalidRows.length > 0 && (
            <Chip
              label={`${invalidRows.length} invalid`}
              color="error"
              size="small"
            />
          )}
          {duplicatesRemoved > 0 && (
            <Chip
              label={`${duplicatesRemoved} duplicate${duplicatesRemoved > 1 ? "s" : ""} removed`}
              color="warning"
              size="small"
            />
          )}
        </Box>
      </Box>

      {/* Hint alerts — idle phase only */}
      {uploadStatus === "idle" && (
        <>
          {invalidRows.length > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Invalid rows (missing or malformed email, CSV duplicates) will be
              skipped. Hover the{" "}
              <ErrorIcon
                color="error"
                fontSize="inherit"
                sx={{ verticalAlign: "middle" }}
              />{" "}
              icon to see the issue. Double-click any cell to edit.
            </Alert>
          )}
          {existingRows.length > 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Existing users{" "}
              <Person
                color="info"
                fontSize="inherit"
                sx={{ verticalAlign: "middle" }}
              />{" "}
              are already in the organization and will not be re-created. Any
              permissions you select for them will be applied in step 2.
            </Alert>
          )}
        </>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 460 }}>
        <DataGrid
          rows={[...displayRows].sort(
            (a, b) =>
              (b.errors.length > 0 ? 1 : 0) - (a.errors.length > 0 ? 1 : 0),
          )}
          columns={columns}
          columnHeaderHeight={80}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={onProcessRowUpdateError}
          getRowClassName={(params) => {
            const row = params.row as DisplayRow;
            if (row.errors.length > 0) return "csv-row-error";
            if (row.isExisting) return "csv-row-existing";
            return "";
          }}
          sx={{
            "& .csv-row-error": { backgroundColor: "rgba(211, 47, 47, 0.06)" },
            "& .csv-row-error:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.12) !important",
            },
            "& .csv-row-existing": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
            "& .csv-row-existing:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08) !important",
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>
    </>
  );
};
